// src/services/socialMediaService.ts

export interface SocialMediaPost {
  id: string;
  platform: 'twitter' | 'facebook' | 'instagram' | 'whatsapp';
  author: string;
  content: string;
  timestamp: string;
  location: string | null;
  disasterType: string | null;
  severity: 'critical' | 'high' | 'medium' | 'low';
  likes: number;
  shares: number;
}

class SocialMediaService {
  private static instance: SocialMediaService;

  static getInstance(): SocialMediaService {
    if (!SocialMediaService.instance) {
      SocialMediaService.instance = new SocialMediaService();
    }
    return SocialMediaService.instance;
  }

  // Returns mock data that looks like real social media posts
  async getDisasterPosts(): Promise<SocialMediaPost[]> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));

    return [
      {
        id: '1',
        platform: 'twitter',
        author: '@RescueAlert',
        content: 'URGENT: Severe flooding in Zone A! Water level rising rapidly. Multiple people trapped on rooftops. #ZoneA #Flood #Emergency',
        timestamp: new Date().toISOString(),
        location: 'Zone A',
        disasterType: 'flood',
        severity: 'critical',
        likes: 234,
        shares: 89
      },
      {
        id: '2',
        platform: 'facebook',
        author: 'Community Watch',
        content: 'Fire reported in Zone B market area. Smoke visible from distance. Emergency services notified. Avoid the area.',
        timestamp: new Date(Date.now() - 5 * 60000).toISOString(),
        location: 'Zone B',
        disasterType: 'fire',
        severity: 'high',
        likes: 89,
        shares: 34
      },
      {
        id: '3',
        platform: 'twitter',
        author: '@EarthquakeUpdates',
        content: 'Magnitude 4.2 earthquake felt in Zone C. No immediate reports of damage but people advised to stay alert.',
        timestamp: new Date(Date.now() - 15 * 60000).toISOString(),
        location: 'Zone C',
        disasterType: 'earthquake',
        severity: 'medium',
        likes: 156,
        shares: 67
      },
      {
        id: '4',
        platform: 'whatsapp',
        author: 'Neighborhood Group',
        content: '⚠️ Building collapse in Zone C! Multiple people possibly trapped. Sending help immediately.',
        timestamp: new Date(Date.now() - 25 * 60000).toISOString(),
        location: 'Zone C',
        disasterType: 'collapse',
        severity: 'critical',
        likes: 45,
        shares: 23
      },
      {
        id: '5',
        platform: 'instagram',
        author: 'weather_warnings',
        content: 'Cyclone approaching coastal areas. Wind speeds increasing. Prepare for possible evacuation. #Cyclone #StaySafe',
        timestamp: new Date(Date.now() - 35 * 60000).toISOString(),
        location: 'Coastal Zone',
        disasterType: 'cyclone',
        severity: 'high',
        likes: 567,
        shares: 123
      }
    ];
  }

  // Get posts filtered by disaster type
  async getPostsByDisasterType(type: string): Promise<SocialMediaPost[]> {
    const posts = await this.getDisasterPosts();
    return posts.filter(post => post.disasterType === type);
  }

  // Get critical posts only
  async getCriticalPosts(): Promise<SocialMediaPost[]> {
    const posts = await this.getDisasterPosts();
    return posts.filter(post => post.severity === 'critical');
  }

  // Get recent posts (last hour)
  async getRecentPosts(): Promise<SocialMediaPost[]> {
    const posts = await this.getDisasterPosts();
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    return posts.filter(post => new Date(post.timestamp).getTime() > oneHourAgo);
  }
}

export default SocialMediaService.getInstance();